import React, { useState, useEffect, useCallback, useRef } from 'react'
import { getSession } from '../../getSession'
import { getVoiceDni, getVoicePhonedetails, sendMassVoiceNew } from '../../utils/lead'
import Swal from 'sweetalert2'

const VoiceBroadCast = ({
  onClose = () => {},
  onSend = (payload) => {},
  initialRecipients = { mobile1: true, mobile2: false, mobile3: false },
  selectedCount = 0,
  selectedLeadIds = [],
  hashedIds = '',
  massOpBatchId = '',
  applyOnAllLeads = false,
  registerSubmitHandler
}) => {
  const [schedule, setSchedule] = useState(false)
  const [scheduleAt, setScheduleAt] = useState('')
  const [recipients, setRecipients] = useState(initialRecipients)
  const [transactionalType, setTransactionalType] = useState('promotional')
  const [dni, setDni] = useState('')
  const [appFlow, setAppFlow] = useState('')
  const [duration, setDuration] = useState('60')
  const [error, setError] = useState('')

  // allow parent to override via prop in future if needed
  const [dniOptions, setDniOptions] = useState([])
  const [appFlowOptions, setAppFlowOptions] = useState([])
  const handleSendRef = useRef(null)

  const toggleRecipient = (key) => setRecipients(prev => ({ ...prev, [key]: !prev[key] }))

  const validate = () => {
    setError('')
    if (!Object.values(recipients).some(Boolean)) {
      setError('Select at least one recipient (Mobile1/2/3).')
      return false
    }
    if (!dni) {
      setError('Choose DNI Number.')
      return false
    }
    if (!appFlow) {
      setError('Choose App Flow.')
      return false
    }
    if ((Number(duration) || 0) <= 0) {
      setError('Provide voice duration.')
      return false
    }
    if (schedule && !scheduleAt) {
      setError('Select schedule date and time.')
      return false
    }

    const numericBatchId = Number(massOpBatchId) || 0
    const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.join('/') : '') || '').trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0

    if (applyOnAllLeads && numericBatchId <= 0) {
      setError('Invalid request. Missing mass batch id for Apply on all.')
      return false
    }

    if (!shouldUseBatchId && !idsFromSelection) {
      setError('Invalid request. Select leads or provide hashed ids.')
      return false
    }

    return true
  }

  useEffect(() => {
    let mounted = true
    const fetchDnis = async () => {
      try {
        const { userId, TokenId } = getSession()
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { UserId: userId },
          BroadcastName: ''
        }
        const resp = await getVoiceDni(payload)
        let list = resp ?? []
        if (list && list.data) list = list.data
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Details ?? list
        }
        const arr = Array.isArray(list) ? list.map(item => {
          if (!item) return null
          if (typeof item === 'string') {
            return { value: item, label: item }
          }

          const value = item.DnidNo ?? item.DniNm ?? item.PhoneNo ?? item.Dni ?? item.DNI ?? item.Code ?? item.Value ?? ''
          const label = item.DniNm ?? item.DnidNo ?? item.PhoneNo ?? item.Dni ?? item.DNI ?? item.Text ?? String(value)
          if (!value) return null
          return { value: String(value), label: String(label) }
        }).filter(Boolean) : []
        if (mounted) {
          setDniOptions([{ value: '', label: '---Choose DNI Number---' }, ...arr])
          if (arr.length) setDni(arr[0].value)
        }
      } catch (err) {
        console.warn('Failed to load Voice DNI options', err?.message || err)
        if (mounted) setDniOptions([{ value: '', label: '---Choose DNI Number---' }])
      }
    }
    fetchDnis()
    return () => { mounted = false }
  }, [])

  // fetch app flows (VoicePhonedetails)
  useEffect(() => {
    let mounted = true
    const fetchAppFlows = async () => {
      try {
        const { userId, TokenId, parentId } = getSession()
        const payload = {
          Token: TokenId,
          Message: '',
          LoggedUserId: userId,
          MAC_Address: '',
          IP_Address: '',
          Details: { Parentid: parentId ?? 0 },
          BroadcastName: ''
        }
        const resp = await getVoicePhonedetails(payload)
        let list = resp ?? []
        if (list && list.data) list = list.data
        if (list && !Array.isArray(list)) {
          list = list.ResultList ?? list.Items ?? list.Data ?? list.Details ?? list
        }
        const arr = Array.isArray(list) ? list.map(item => {
          if (!item) return null
          if (typeof item === 'string') {
            const numeric = Number(item)
            if (numeric > 0) {
              return { value: String(numeric), label: item }
            }
            return null
          }

          const value = item.AppId ?? item.AppID ?? item.Id ?? item.ID ?? item.Code ?? item.Value ?? 0
          const label = item.AppName ?? item.Name ?? item.Text ?? String(value)
          if ((Number(value) || 0) <= 0) return null
          return { value: String(value), label: String(label) }
        }).filter(Boolean) : []
        if (mounted) {
          setAppFlowOptions([{ value: '', label: '---Choose App Flow---' }, ...arr])
          if (arr.length) setAppFlow(arr[0].value)
        }
      } catch (err) {
        console.warn('Failed to load Voice phone details (app flows)', err?.message || err)
        if (mounted) setAppFlowOptions([{ value: '', label: '---Choose App Flow---' }])
      }
    }
    fetchAppFlows()
    return () => { mounted = false }
  }, [])

  const handleBroadcast = useCallback(async () => {
    if (!validate()) return

    const { userId, TokenId } = getSession()
    const numericUserId = Number(userId) || 0
    const numericBatchId = Number(massOpBatchId) || 0
    const idsFromSelection = (hashedIds || (Array.isArray(selectedLeadIds) ? selectedLeadIds.join('/') : '') || '').trim()
    const shouldUseBatchId = !!applyOnAllLeads && numericBatchId > 0
    const idsForPayload = shouldUseBatchId ? '' : idsFromSelection

    const payload = {
      Token: TokenId,
      Message: '',
      LoggedUserId: numericUserId,
      MAC_Address: '',
      IP_Address: '',
      Details: {
        IDS: idsForPayload,
        Userid: numericUserId,
        massbatchid: shouldUseBatchId ? numericBatchId : 0,
        Module: 'Lead',
        ModuleType: 'lead',
        StartDateTime: schedule ? (scheduleAt || '') : '',
        DnidNo: String(dni || ''),
        AppId: Number(appFlow) || 0,
        Duration: Number(duration) || 0,
        IsMobile: !!recipients.mobile1,
        IsMobile1: !!recipients.mobile2,
        IsMobile2: !!recipients.mobile3,
        IsPromotional: transactionalType === 'promotional'
      },
      BroadcastName: ''
    }

    try {
      const resp = await sendMassVoiceNew(payload)
      const status = Number(resp?.Status ?? resp?.status ?? 0)
      const details = resp?.Details
      const detailMessage = typeof details === 'string'
        ? details
        : (Array.isArray(details)
          ? (details[0]?.Result || details[0]?.Message || '')
          : (details?.Result || details?.Message || ''))

      if (status === 1) {
        Swal.fire({ icon: 'success', title: 'Sent', text: 'Voice broadcast sent successfully.' })
        onSend(payload)
        onClose()
      } else {
        Swal.fire({ icon: 'error', title: 'Error', text: detailMessage || resp?.Message || 'Failed to send voice broadcast.' })
      }
    } catch (err) {
      console.error('send mass voice error', err)
      Swal.fire({ icon: 'error', title: 'Error', text: 'An error occurred while sending voice broadcast.' })
    }
  }, [
    recipients,
    transactionalType,
    dni,
    appFlow,
    duration,
    schedule,
    scheduleAt,
    massOpBatchId,
    hashedIds,
    selectedLeadIds,
    applyOnAllLeads,
    onClose,
    onSend
  ])

  useEffect(() => {
    handleSendRef.current = handleBroadcast
  }, [handleBroadcast])

  useEffect(() => {
    if (typeof registerSubmitHandler === 'function') {
      registerSubmitHandler(() => () => handleSendRef.current?.())
      return () => registerSubmitHandler(null)
    }
  }, [registerSubmitHandler])

  return (
    <div className="w-full max-w-2xl">
      <div className="mb-3 text-sm text-blue-700 bg-blue-50 p-3 rounded">Info! You are about to perform a mass action on {selectedCount} record(s)</div>

      <div className="mb-3">
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={schedule} onChange={() => setSchedule(s => !s)} className=" rounded-[3px] mr-2" />
          On Schedule Date And Time
        </label>
      </div>

      {schedule && (
        <div className="mb-4">
          <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)} className="w-full border px-3 py-2 rounded" />
        </div>
      )}

      <div className="mb-4">
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={recipients.mobile1} onChange={() => toggleRecipient('mobile1')} className=" rounded-[3px] mr-2" />
          Mobile1
        </label>
        <label className="inline-flex items-center mr-4">
          <input type="checkbox" checked={recipients.mobile2} onChange={() => toggleRecipient('mobile2')} className=" rounded-[3px] mr-2" />
          Mobile2
        </label>
        <label className="inline-flex items-center">
          <input type="checkbox" checked={recipients.mobile3} onChange={() => toggleRecipient('mobile3')} className=" rounded-[3px] mr-2" />
          Mobile3
        </label>
      </div>

      <div className="mb-3">
        <div className="mb-2 font-medium">Transactional Type :</div>
        <label className="inline-flex items-center mr-4">
          <input type="radio" name="txn-type" value="promotional" checked={transactionalType === 'promotional'} onChange={() => setTransactionalType('promotional')} className="mr-2" />
          Promotional
        </label>
        <label className="inline-flex items-center">
          <input type="radio" name="txn-type" value="transactional" checked={transactionalType === 'transactional'} onChange={() => setTransactionalType('transactional')} className="mr-2" />
          Transactional
        </label>
      </div>

      <div className="space-y-3">
        <select value={dni} onChange={e => setDni(e.target.value)} className="w-full border px-3 py-2 rounded">
          {dniOptions.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>

        <select value={appFlow} onChange={e => setAppFlow(e.target.value)} className="w-full border px-3 py-2 rounded">
          {appFlowOptions.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>

        <input
          type="number"
          min="1"
          value={duration}
          onChange={e => setDuration(e.target.value)}
          className="w-full border px-3 py-2 rounded"
          placeholder="Duration (seconds)"
        />
      </div>

      {error && <div className="text-red-600 text-sm mt-3">{error}</div>}

    </div>
  )
}

export default VoiceBroadCast
